// Analyzer registration & extension-based resolution (ARCHITECTURE.md §6.4).
//
// AnalyzerRegistry maps file extensions and analyzer ids (the `language` field)
// to LanguageAnalyzer instances. Registration order determines priority: when
// multiple analyzers claim the same extension or id, the first registered wins.
// The registry holds no parser logic — it is a pure lookup table feeding the
// AST Graph Builder (§6.7).

import { extname } from "node:path";

import type { LanguageAnalyzer } from "./types.js";
import type { ProjectAnalyzer } from "./project-analyzer.js";

/**
 * Fallback extension map keyed by analyzer `language`, used only when an
 * analyzer does not declare its own `fileExtensions`. Built-in analyzers
 * declare extensions explicitly, so this is a safety net for minimal analyzers.
 */
const DEFAULT_EXTENSIONS: Readonly<Record<string, readonly string[]>> = {
  typescript: [".ts", ".tsx", ".js", ".jsx", ".mts", ".cts", ".mjs", ".cjs"],
  yaml: [".yaml", ".yml"],
};

/** Normalise an extension to a leading-dot, lower-case form (".TS" → ".ts"). */
function normalizeExtension(ext: string): string {
  const lower = ext.toLowerCase();
  return lower.startsWith(".") ? lower : `.${lower}`;
}

/** Resolve the extensions an analyzer claims, preferring explicit declarations. */
function resolveExtensions(analyzer: LanguageAnalyzer): readonly string[] {
  if (analyzer.fileExtensions && analyzer.fileExtensions.length > 0) {
    return analyzer.fileExtensions;
  }
  return DEFAULT_EXTENSIONS[analyzer.language] ?? [];
}

export class AnalyzerRegistry {
  /** Normalised extension → analyzer (first registration wins). */
  private readonly byExtension = new Map<string, LanguageAnalyzer>();

  /** Analyzer id (`language`) → analyzer (first registration wins). */
  private readonly byId = new Map<string, LanguageAnalyzer>();

  /** All registered analyzers, in registration order. */
  private readonly analyzers: LanguageAnalyzer[] = [];

  /** Registered ProjectAnalyzers (cross-file resolution), in registration order. */
  private readonly projectAnalyzers: ProjectAnalyzer[] = [];

  /**
   * Register an analyzer by its file extensions and id. Extensions come from
   * `analyzer.fileExtensions` when present, otherwise a fallback map keyed by
   * `analyzer.language`. Re-registering an already-claimed extension or id is a
   * no-op for that key — the first registered analyzer keeps priority.
   */
  register(analyzer: LanguageAnalyzer): void {
    this.analyzers.push(analyzer);

    if (!this.byId.has(analyzer.language)) {
      this.byId.set(analyzer.language, analyzer);
    }

    for (const ext of resolveExtensions(analyzer)) {
      const norm = normalizeExtension(ext);
      if (!this.byExtension.has(norm)) {
        this.byExtension.set(norm, analyzer);
      }
    }
  }

  /** Resolve the analyzer for a file path by its extension. */
  getForFile(filePath: string): LanguageAnalyzer | undefined {
    const ext = extname(filePath).toLowerCase();
    if (ext === "") {
      return undefined;
    }
    return this.byExtension.get(ext);
  }

  /** Look up a registered analyzer by its id (the `language` field). */
  getById(id: string): LanguageAnalyzer | undefined {
    return this.byId.get(id);
  }

  /** List every registered analyzer, in registration order. */
  listAll(): LanguageAnalyzer[] {
    return [...this.analyzers];
  }

  /**
   * Register a ProjectAnalyzer for cross-file resolution (proposal §3.5). These
   * run after the per-file analysis pass to rewrite bare-name `calls` /
   * `type_reference` edges to cross-file symbolIds.
   */
  registerProjectAnalyzer(analyzer: ProjectAnalyzer): void {
    this.projectAnalyzers.push(analyzer);
  }

  /** List every registered ProjectAnalyzer, in registration order. */
  listProjectAnalyzers(): ProjectAnalyzer[] {
    return [...this.projectAnalyzers];
  }
}
